import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useValue } from "@legendapp/state/react";
import { SwipeableVocabularyList } from "./swipeable-vocabulary-list";
import { WordReadingFluency } from "./word-reading-fluency";
import { Button } from "@bindery/ui/components/ui/button";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { useInputType } from "@bindery/ui/hooks/use-input-type";
import { useLogger } from "@bindery/logging/react";
import {
  uiStateStore$,
  vocabularyExerciseStore$,
} from "@/ear-reader/state/stores";
import {
  VocabularyExerciseProvider,
  useVocabularyExercise,
} from "./vocabulary-exercise-context";
import type { WordListItem } from "@bindery/shared/reader/page-parsing";
import type { VocabularyWord } from "@/ear-reader/types/vocabulary-exercise";

// Re-exports for consumers

/** @deprecated Import VocabularyWord from @/lib/types/vocabulary-exercise instead */
export type Word = VocabularyWord;
export type { WordListItem };

/**
 * Props for SightVocabularyExercise.
 *
 * NOTE: The `words` prop should be stable (memoized or constant) for the component's
 * lifetime. If `words` changes, the exercise restarts. To preserve progress across
 * word list changes, unmount/remount with a `key` prop.
 */
export interface SightVocabularyExerciseProps {
  words: WordListItem[];
  onEngageWRF?: (word: WordListItem) => void;
  onComplete?: (results: VocabularyWord[]) => void;
}

// InteractionTutorial — teaches swipe/keyboard gestures

const InteractionTutorial: React.FC<{
  inputType: "touch" | "mouse";
  onDismiss: () => void;
  dismissed: boolean;
}> = ({ inputType, onDismiss, dismissed }) => {
  if (dismissed) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm dark:border-indigo-700 dark:bg-indigo-900/30"
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- <output> is a form-result element; this is a tutorial hint
      role="status"
    >
      {inputType === "touch" ? (
        <div className="flex items-center">
          <div className="mr-2">
            <div className="relative flex h-8 w-16 items-center justify-center rounded-lg bg-gray-200">
              <div className="animate-pulse-horizontal absolute h-4 w-4 rounded-full bg-white"></div>
            </div>
          </div>
          <div>
            <strong>Swipe right</strong> on words you recognize,{" "}
            <strong>swipe left</strong> on words you don&apos;t.
          </div>
        </div>
      ) : (
        <div className="flex items-center">
          <div className="mr-2">
            <div className="relative flex h-8 w-16 items-center justify-center rounded-lg bg-gray-200">
              <div className="h-4 w-4 rounded-full bg-white"></div>
              <ArrowRightIcon className="absolute right-2 h-4 w-4 animate-pulse text-green-600 dark:text-green-400" />
              <ArrowLeftIcon className="absolute left-2 h-4 w-4 animate-pulse text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div>
            <strong>Hover</strong> over words to reveal actions, or use{" "}
            <strong>arrow keys</strong> to navigate and <strong>Space</strong>{" "}
            for Next &gt;
          </div>
        </div>
      )}
      <button
        onClick={onDismiss}
        className="mt-2 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
        aria-label="Dismiss tutorial"
      >
        Got it
      </button>
    </div>
  );
};

// SightVocabularyExerciseInner — exercise UI that consumes the provider

/**
 * Inner component that reads state from VocabularyExerciseContext.
 * Separated from the provider so state management is decoupled from UI.
 *
 * Reactivity note: The context state object is rebuilt by the provider whenever
 * any of the underlying useValue() subscriptions change, which triggers a
 * re-render of this component with fresh values. The destructured values
 * are plain snapshots, but they're kept current by the provider's reactivity.
 */
const SightVocabularyExerciseInner: React.FC<{
  words: WordListItem[];
  onComplete?: (results: VocabularyWord[]) => void;
}> = ({ words, onComplete }) => {
  const inputType = useInputType();
  const log = useLogger();
  const exerciseStartRef = useRef<number>(0);
  const [resetKey, setResetKey] = useState(0);

  const { state, actions } = useVocabularyExercise();
  const { wordList, activeIndex, reviewIndex, isDrawerOpen } = state;
  const { setActiveIndex, setReviewIndex, onStatusChange, markDisfluency } =
    actions;

  // Read tutorial dismissed state reactively
  const tutorialDismissed = useValue(uiStateStore$.tutorialDismissed);

  // inputType is read through a ref by the init effect: it flips touch↔mouse
  // on the first touch/mouse event, which must not restart the exercise.
  const inputTypeRef = useRef(inputType);
  useEffect(() => {
    inputTypeRef.current = inputType;
  }, [inputType]);

  // --- Initialize store on mount ---
  // The `words` prop is expected to be stable for the component lifetime.
  // If `words` changes, the effect re-runs and the exercise restarts.
  useEffect(() => {
    vocabularyExerciseStore$.reviewIndex.set(null);
    vocabularyExerciseStore$.activeIndex.set(0);
    vocabularyExerciseStore$.wordList.set(
      words.map((word) => ({
        ...word,
        status: "unreviewed" as const,
      })),
    );
    exerciseStartRef.current = Date.now();

    // Event name uses reading.vocabulary.* namespace (extends the reading domain)
    log.event("reading.vocabulary.exercise.started", {
      total_words: words.length,
      input_type: inputTypeRef.current,
    });

    return () => {
      const currentWords = vocabularyExerciseStore$.wordList.peek();
      const reviewed = currentWords.filter(
        (w) => w.status !== "unreviewed",
      ).length;
      const total = currentWords.length;

      if (total > 0 && reviewed < total) {
        log.event("reading.vocabulary.exercise.abandoned", {
          reviewed_count: reviewed,
          total_words: total,
          duration_ms: Date.now() - exerciseStartRef.current,
        });
      }
    };
  }, [words, log]);

  // --- Handlers ---

  const handleComplete = useCallback(() => {
    // Use peek() for non-reactive read of current store state,
    // ensuring we log the actual final state, not a stale snapshot.
    const currentWordList = vocabularyExerciseStore$.wordList.peek();
    const recognized = currentWordList.filter(
      (w) => w.status === "recognized",
    ).length;
    const unrecognized = currentWordList.filter(
      (w) => w.status === "unrecognized",
    ).length;

    log.event("reading.vocabulary.exercise.complete", {
      recognized_count: recognized,
      unrecognized_count: unrecognized,
      total_words: currentWordList.length,
      completion_rate:
        currentWordList.length > 0 ? recognized / currentWordList.length : 0,
      duration_ms: Date.now() - exerciseStartRef.current,
    });

    onComplete?.(currentWordList);
  }, [onComplete, log]);

  const handleTutorialDismiss = useCallback(() => {
    uiStateStore$.tutorialDismissed.set(true);
    log.event("reading.vocabulary.tutorial.dismissed", {
      input_type: inputType,
    });
  }, [log, inputType]);

  const resetAllWords = useCallback(() => {
    const currentWordList = vocabularyExerciseStore$.wordList.peek();
    vocabularyExerciseStore$.wordList.set(
      currentWordList.map((w) => ({ ...w, status: "unreviewed" as const })),
    );
    vocabularyExerciseStore$.activeIndex.set(0);
    vocabularyExerciseStore$.reviewIndex.set(null);
    // Reset timer for duration tracking
    exerciseStartRef.current = Date.now();
    // Force WordReadingFluency to remount and reinitialize timer reducer
    setResetKey((k) => k + 1);

    log.event("reading.vocabulary.exercise.reset", {
      total_words: currentWordList.length,
    });
  }, [log]);

  // --- Derived state ---

  const isReviewMode = activeIndex !== null && activeIndex >= wordList.length;
  const reviewIndexWithDefault =
    isReviewMode && reviewIndex === null ? 0 : reviewIndex;

  // Compute reviewed count once and reuse for progress display
  const reviewedCount = useMemo(
    () => wordList.filter((w) => w.status !== "unreviewed").length,
    [wordList],
  );
  const progressPercentage =
    wordList.length > 0 ? (reviewedCount / wordList.length) * 100 : 0;

  // Check length first (O(1)) before iterating wordList (O(n))
  const allReviewed =
    activeIndex !== null &&
    activeIndex >= wordList.length &&
    wordList.every((w) => w.status !== "unreviewed");

  // --- Render ---

  return (
    <div
      className="mx-auto w-full p-4 sm:max-w-md md:max-w-lg lg:max-w-xl"
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a labelled landmark around the review list
      role="region"
      aria-label="Vocabulary review list"
    >
      <InteractionTutorial
        inputType={inputType}
        onDismiss={handleTutorialDismiss}
        dismissed={tutorialDismissed}
      />
      <div className="sr-only" aria-live="polite">
        Use up and down arrow keys to navigate between words. Use left and right
        arrow keys to mark words as recognized or not recognized.
      </div>
      {/* Progress bar */}
      <div
        className="mb-6 h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a styled track whose fill is a child div; <progress> cannot be restyled this way across browsers
        role="progressbar"
        aria-valuenow={progressPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${reviewedCount} of ${wordList.length} words reviewed`}
      >
        <div
          className="h-2.5 rounded-full bg-indigo-600 transition-all duration-300 dark:bg-indigo-500"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
      {allReviewed ? (
        <Button
          onClick={handleComplete}
          className="min-h-touch-generous mb-6 w-full border-2 border-gray-500 bg-indigo-100 text-3xl text-gray-700 hover:bg-indigo-200 dark:border-gray-600 dark:bg-indigo-900/30 dark:text-gray-200 dark:hover:bg-indigo-800/40"
          aria-label="Continue"
        >
          Continue
        </Button>
      ) : (
        <>
          <p className="mb-6 text-center text-gray-500 italic">
            {inputType === "touch"
              ? "Swipe right on words you recognize, left to build fluency"
              : "Use the right arrow key to skip the words you know, left to build fluency"}
          </p>
          <WordReadingFluency
            key={resetKey}
            words={wordList}
            onStatusChange={onStatusChange}
            markDisfluency={markDisfluency}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            isDrawerOpen={isDrawerOpen}
            log={log}
          />
        </>
      )}
      <SwipeableVocabularyList
        words={wordList}
        onStatusChange={onStatusChange}
        focused={isReviewMode}
        activeIndex={activeIndex}
        reviewIndex={reviewIndexWithDefault}
        setReviewIndex={setReviewIndex}
        isDrawerOpen={isDrawerOpen}
        log={log}
      />
      <div className="mt-6 flex flex-col space-y-4" aria-live="polite">
        {reviewedCount > 0 && (
          <Button
            variant="outline"
            onClick={resetAllWords}
            className="min-h-touch-min w-full"
            aria-label="Reset all words"
          >
            Reset All
          </Button>
        )}
      </div>
    </div>
  );
};

// SightVocabularyExercise — public entry point with provider

/**
 * Manages the word list, progress, and switching between fluency and review
 * modes. Wraps the inner exercise UI with VocabularyExerciseProvider so
 * state is accessible to all child components via context.
 */
export const SightVocabularyExercise: React.FC<
  SightVocabularyExerciseProps
> = ({ words, onEngageWRF, onComplete }) => {
  return (
    <VocabularyExerciseProvider onEngageWRF={onEngageWRF}>
      <SightVocabularyExerciseInner words={words} onComplete={onComplete} />
    </VocabularyExerciseProvider>
  );
};

export default SightVocabularyExercise;
